import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Droplets, Leaf, Recycle, Sun, Award } from "lucide-react";
import TeamSection from "@/components/landing/TeamSection";

const ease = [0.22, 1, 0.36, 1];

function PageShell({ overline, titleA, titleB, intro, children, testid }) {
  return (
    <section data-testid={testid} className="mx-auto max-w-[1440px] px-6 pb-28 pt-32 lg:px-12 lg:pt-40">
      <motion.p
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease }}
        className="text-xs uppercase tracking-[0.3em] text-harvest"
      >
        {overline}
      </motion.p>
      <motion.h1
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.1, ease }}
        className="mt-4 max-w-4xl font-display text-4xl font-medium leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl"
      >
        {titleA} <span className="italic text-harvest">{titleB}</span>
      </motion.h1>
      {intro && (
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease }}
          className="mt-6 max-w-2xl text-sm leading-relaxed text-sage sm:text-base"
        >
          {intro}
        </motion.p>
      )}
      {children}
    </section>
  );
}

const HISTORY = [
  { year: "1998", title: "Shuruaat — Jaipur VKI", text: "Surendra Gajraj ne V.K.I. Area, Jaipur mein ek chhote manufacturing unit se Jamna Industries ki niv rakhi — sirf ek machine, ek mission: kisan tak bharosemand pipe pahunchana." },
  { year: "2005", title: "Mini Sprinkler Range", text: "Rajasthan ke gehun aur moongphali ke kisano ki demand par mini sprinkler systems ka production shuru — pehla season hi poora stock bik gaya." },
  { year: "2012", title: "Drip Systems + ISI Certification", text: "Drip irrigation ki poori range launch hui aur saare main products ISI-grade certified hue — quality ab kagaz par bhi proved." },
  { year: "2018", title: "500+ Dealer Network", text: "Rajasthan, MP, Gujarat aur Haryana tak dealer network phaila — har zile mein Jamna ka saathi." },
  { year: "2026", title: "A-to-Z Irrigation Catalogue", text: "Aaj Jamna ke paas HDPE pipes se lekar foggers tak 200+ models ka poora catalogue hai — aur 10,000+ kisanon ka bharosa." },
];

export function HistoryPage() {
  return (
    <PageShell
      testid="history-page"
      overline="Who We Are — Hamara Itihas"
      titleA="Chhote workshop se"
      titleB="bharose ka brand."
      intro="Har badi company ek din chhoti hoti hai. Jamna ki kahani bhi waise hi likhi ja rahi hai — kadam dar kadam, kisan ke saath."
    >
      <div className="mt-16 space-y-0" data-testid="history-timeline">
        {HISTORY.map((h, i) => (
          <motion.div
            key={h.year}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: (i % 3) * 0.06, ease }}
            className="grid gap-4 border-t border-line py-10 sm:grid-cols-12"
          >
            <p className="font-display text-4xl font-medium italic text-harvest sm:col-span-3 lg:text-5xl">{h.year}</p>
            <div className="sm:col-span-9">
              <p className="font-display text-2xl font-medium tracking-tight text-cream">{h.title}</p>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-sage">{h.text}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </PageShell>
  );
}

export function LeadershipPage() {
  return <TeamSection />;
}

const SUSTAIN = [
  { icon: Droplets, title: "Jal Sanrakshan", stat: "60% tak", text: "Jamna drip aur mini sprinkler systems flood irrigation ke muqable 60% tak paani bachate hain — har boond ka hisaab." },
  { icon: Sun, title: "Clean Energy Works", stat: "Solar-ready", text: "Hamare Jaipur works par solar-ready operations aur energy-efficient extrusion lines — kam carbon, zyada kaam." },
  { icon: Recycle, title: "Recyclable Material", stat: "100% virgin + recyclable", text: "Saare HDPE/PP products virgin food-grade polymer se bante hain aur poori tarah recyclable hain — mitti ke liye safe." },
  { icon: Award, title: "PMKSY Support", stat: "Subsidy-ready", text: "Pradhan Mantri Krishi Sinchayee Yojana ke norms ke hisaab se subsidy-ready designs — documents mein bhi poori madad." },
];

export function SustainabilityPage() {
  return (
    <PageShell
      testid="sustainability-page"
      overline="Who We Are — Sustainability"
      titleA="Aaj ki bachat,"
      titleB="kal ki kamai."
      intro="Jamna ka maanna hai — jo technology paani, bijli aur mitti bachaye, wahi asli vikas hai. Isi soch ke saath har product design hota hai."
    >
      <div className="mt-16 grid gap-4 sm:grid-cols-2" data-testid="sustainability-grid">
        {SUSTAIN.map((s, i) => (
          <motion.div
            key={s.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: (i % 2) * 0.08, ease }}
            className="border border-line bg-moss p-8"
          >
            <s.icon className="h-8 w-8 text-harvest" strokeWidth={1.5} />
            <p className="mt-6 font-display text-2xl font-medium tracking-tight text-cream">{s.title}</p>
            <p className="mt-1 text-xs uppercase tracking-[0.2em] text-harvest">{s.stat}</p>
            <p className="mt-4 text-sm leading-relaxed text-sage">{s.text}</p>
          </motion.div>
        ))}
      </div>
    </PageShell>
  );
}

const SEGMENTS = [
  { name: "Micro Irrigation", hindi: "सूक्ष्म सिंचाई", text: "Drip, mini/micro sprinkler aur foggers — open field se lekar orchard tak, har crop ke liye precision irrigation.", to: "/products/drip-systems" },
  { name: "Agri Inputs", hindi: "कृषि आदान", text: "Filters, venturi injectors aur fertilizer tanks — paani ke saath khad ka sahi dose, seedha jad tak.", to: "/products/filters" },
  { name: "Building & Construction", hindi: "निर्माण", text: "PVC/HDPE pressure pipes, casing pipes aur fittings — ghar aur project plumbing ke liye ISI-grade range.", to: "/products/pvc-pipes" },
  { name: "Infrastructure & Public Utilities", hindi: "इन्फ्रास्ट्रक्चर", text: "Large diameter HDPE pipes, sewerage aur drainage solutions — jal board aur municipal projects ke liye.", to: "/products/hdpe-pipes" },
  { name: "Industrial", hindi: "औद्योगिक", text: "Chemical-resistant piping, valves aur fittings — factory process lines aur industrial water handling ke liye.", to: "/products/fittings-valves" },
  { name: "End-to-End Solutions", hindi: "टर्नकी सॉल्यूशन", text: "Survey se lekar installation tak — poora sprinkler/drip project, subsidy paperwork ke saath, ek hi vendor.", to: "/contact" },
];

export function SegmentsPage() {
  return (
    <PageShell
      testid="segments-page"
      overline="Segments — Kiske Liye"
      titleA="Har kshetra ke liye"
      titleB="ek solution."
      intro="Kisaan ho ya contractor, ghar ho ya industrial project — Jamna ki range har segment ko cover karti hai."
    >
      <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="segments-grid">
        {SEGMENTS.map((s, i) => (
          <motion.div
            key={s.name}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: (i % 3) * 0.06, ease }}
          >
            <Link
              to={s.to}
              data-testid={`segment-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="group flex h-full flex-col border border-line bg-moss p-8 transition-all duration-300 hover:-translate-y-1 hover:border-harvest"
            >
              <p className="text-[11px] uppercase tracking-[0.2em] text-sage">{s.hindi}</p>
              <p className="mt-2 font-display text-2xl font-medium tracking-tight text-cream transition-colors duration-300 group-hover:text-harvest">
                {s.name}
              </p>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-sage">{s.text}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-harvest">
                Explore <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
    </PageShell>
  );
}
